import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { samplesAPI, parametersAPI, usersAPI } from '../../api';
import { Loading, Pagination, StatusBadge, Modal } from '../../components';
import { usePagination } from '../../hooks';
import { useToast } from '../../context/ToastContext';
import './Samples.css';

/**
 * Samples Page - Admin Dashboard
 *
 * testInfo-based Workflow:
 * - fieldTested=true, labTested=false: Waiting for LAB test
 * - labTested=true, published=false: LAB done, ready to publish
 * - published=true: Sample is public
 */
const Samples = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'all';
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialStatus); // 'all', 'field_only', 'lab_done', 'published'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSample, setSelectedSample] = useState(null);
  const [collectorFilter, setCollectorFilter] = useState(''); // Filter by collector (team member)
  const [teamMembers, setTeamMembers] = useState([]);
  const pagination = usePagination(1, 10);

  // New filter states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [locationTypeFilter, setLocationTypeFilter] = useState('');
  const [waterSourceFilter, setWaterSourceFilter] = useState('');
  const [locationTypes, setLocationTypes] = useState([]);
  const [waterSources, setWaterSources] = useState([]);

  // LAB test modal state
  const [showLabModal, setShowLabModal] = useState(false);
  const [labTargetSample, setLabTargetSample] = useState(null);
  const [labParameters, setLabParameters] = useState([]);
  const [parameterValues, setParameterValues] = useState({});
  const [remark, setRemark] = useState('');
  const [testingLocation, setTestingLocation] = useState('Laboratory'); // On-Site, Laboratory, Hybrid
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Delete confirmation modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch team members and filter options for dropdowns
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [membersRes, sourcesRes] = await Promise.all([
          usersAPI.getTeamMembers(),
          samplesAPI.getSources()
        ]);
        setTeamMembers(membersRes.data.data || []);
        if (sourcesRes.data.data) {
          // Handle both string arrays and object arrays (defensive)
          const rawWaterSources = sourcesRes.data.data.waterSources || [];
          const rawLocationTypes = sourcesRes.data.data.locationTypes || [];
          setWaterSources(rawWaterSources.map(ws => typeof ws === 'string' ? ws : ws.name));
          setLocationTypes(rawLocationTypes.map(lt => typeof lt === 'string' ? lt : lt.name));
        }
      } catch (err) {
        console.log('Failed to load filter options');
      }
    };
    fetchFilterOptions();
  }, []);

  const fetchSamples = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      // Map statusFilter to testInfo query params
      if (statusFilter === 'field_only') {
        params.fieldTested = 'true';
        params.labTested = 'false';
      } else if (statusFilter === 'lab_done') {
        params.labTested = 'true';
        params.published = 'false';
      } else if (statusFilter === 'published') {
        params.published = 'true';
      }
      // 'all' doesn't add any filter

      if (searchQuery) params.search = searchQuery;
      if (collectorFilter) params.collectedBy = collectorFilter;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (locationTypeFilter) params.locationType = locationTypeFilter;
      if (waterSourceFilter) params.waterSource = waterSourceFilter;

      const response = await samplesAPI.getAll(params);
      setSamples(response.data.data);
      pagination.updatePagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load samples');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchQuery, collectorFilter, fromDate, toDate, locationTypeFilter, waterSourceFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSamples();
  }, [fetchSamples]);

  // Open LAB test modal (for FIELD_TESTED samples)
  const openLabTestModal = async (sample) => {
    try {
      setLabTargetSample(sample);
      // Fetch only LAB parameters
      const response = await parametersAPI.getAll(false, 1, 100);
      const labParams = response.data.data.filter(p => p.isActive && p.testLocation === 'LAB');
      setLabParameters(labParams);
      setParameterValues({});
      setRemark(sample.remark || '');
      // Set testing location - default to Hybrid since field test is already done
      setTestingLocation(sample.testingLocation || 'Hybrid');
      setSelectedSample(null);
      setShowLabModal(true);
    } catch (err) {
      toast.error('Failed to load LAB parameters');
    }
  };

  // Handle parameter value change
  const handleParamValueChange = (paramId, value) => {
    setParameterValues(prev => ({
      ...prev,
      [paramId]: value
    }));
  };

  // Submit LAB test values
  const handleLabTestSubmit = async (e) => {
    e.preventDefault();

    // Build parameters array (only params with values filled)
    const parameters = labParameters
      .filter(param => parameterValues[param._id] !== '' && parameterValues[param._id] !== undefined)
      .map(param => ({
        parameterRef: param._id,
        value: isNaN(parameterValues[param._id]) ? parameterValues[param._id] : parseFloat(parameterValues[param._id])
      }));

    if (parameters.length === 0) {
      toast.warning('Please enter at least one LAB parameter value');
      return;
    }

    // Validate remark — must not be empty
    if (!remark || !remark.trim()) {
      toast.warning('Remark is required and cannot be empty');
      return;
    }

    try {
      setSubmitting(true);
      // Send testingLocation along with other data
      await samplesAPI.submitLabTest(labTargetSample._id, parameters, remark.trim(), testingLocation);
      setShowLabModal(false);
      setLabTargetSample(null);
      setParameterValues({});
      setRemark('');
      setTestingLocation('Laboratory');
      fetchSamples();
      toast.success('LAB test submitted and sample published successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit LAB test');
    } finally {
      setSubmitting(false);
    }
  };

  // Publish sample (LAB_TESTED → PUBLISHED)
  const handlePublish = async (sample) => {
    if (!window.confirm(`Publish sample ${sample.sampleId}? This will make it visible to the public.`)) {
      return;
    }
    try {
      await samplesAPI.publish(sample._id);
      fetchSamples();
      setSelectedSample(null);
      toast.success('Sample published successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish sample');
    }
  };

  // Delete sample (soft delete) — opens styled confirmation modal
  const handleDelete = (sample) => {
    setDeleteTarget(sample);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await samplesAPI.delete(deleteTarget._id);
      fetchSamples();
      setSelectedSample(null);
      setDeleteTarget(null);
      toast.success('Sample deleted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete sample');
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    pagination.reset();
    fetchSamples();
  };

  // Download PDF report
  const handleDownloadPDF = async (sample) => {
    try {
      setDownloading(true);
      await samplesAPI.downloadPDF(sample._id, sample.sampleId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Get action buttons based on testInfo status (with fallback for old samples)
  // Returns { middle, danger } — each slot renders a button or a placeholder
  // so every row keeps the same column layout (View | Action | Delete).
  const getActionButtons = (sample) => {
    const testInfo = sample.testInfo || {};

    const hasFieldParams = sample.parameters?.some(p => p.testLocation === 'FIELD');
    const hasLabParams = sample.parameters?.some(p => p.testLocation === 'LAB');

    const isFieldTested = testInfo.fieldTested || hasFieldParams;
    const isLabTested = testInfo.labTested || hasLabParams;
    const isPublished = testInfo.published;

    let middle = null;
    if (isFieldTested && !isLabTested) {
      middle = (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => openLabTestModal(sample)}
        >
          LAB Test
        </button>
      );
    } else if (isLabTested && !isPublished) {
      middle = (
        <button
          className="btn btn-success btn-sm"
          onClick={() => handlePublish(sample)}
        >
          Publish
        </button>
      );
    } else if (isPublished) {
      middle = (
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleDownloadPDF(sample)}
          disabled={downloading}
        >
          PDF
        </button>
      );
    }

    const danger = isPublished ? (
      <button
        className="btn btn-danger btn-sm"
        onClick={() => handleDelete(sample)}
      >
        Delete
      </button>
    ) : null;

    return { middle, danger };
  };

  // Get display status from testInfo (with fallback for old samples)
  const getDisplayStatus = (sample) => {
    const testInfo = sample.testInfo || {};

    // Check testInfo flags first
    if (testInfo.published) return 'PUBLISHED';
    if (testInfo.labTested) return 'LAB_TESTED';
    if (testInfo.fieldTested) return 'FIELD_TESTED';

    // Fallback: If sample has parameters, it was field tested (old samples)
    if (sample.parameters && sample.parameters.length > 0) {
      // Check if any LAB parameters exist
      const hasLabParams = sample.parameters.some(p => p.testLocation === 'LAB');
      if (hasLabParams) return 'LAB_TESTED';
      return 'FIELD_TESTED';
    }

    return 'COLLECTED';
  };

  // Get coordinates from sample
  const getCoordinates = (sample) => {
    if (sample.location?.coordinates) {
      return {
        longitude: sample.location.coordinates[0],
        latitude: sample.location.coordinates[1]
      };
    }
    return null;
  };

  // Status filter options for tabs
  const statusOptions = [
    { key: 'all', label: 'All' },
    { key: 'published', label: 'Published' }
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="samples-page">
      <div className="samples-header">
        <h1>Samples</h1>
        <p>View and manage all water quality samples</p>
      </div>

      <div className="samples-filters">
        {/* Row 1: Search only */}
        <div className="portal-style-controls search-row">
          <div className="search-group">
            <input
              type="text"
              placeholder="Search by address, ID or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input"
            />
          </div>
          <button type="button" className="btn btn-primary" onClick={handleSearch}>Search</button>
        </div>

        {/* Row 2: All filter options */}
        <div className="portal-style-controls filter-row">
          <div className="status-tabs">
            {statusOptions.map(option => (
              <button
                key={option.key}
                className={`tab ${statusFilter === option.key ? 'active' : ''}`}
                onClick={() => { setStatusFilter(option.key); pagination.reset(); }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <select
            className="form-input filter-select"
            value={collectorFilter}
            onChange={(e) => { setCollectorFilter(e.target.value); pagination.reset(); }}
          >
            <option value="">All Collectors</option>
            {teamMembers.map(member => (
              <option key={member._id} value={member._id}>{member.name}</option>
            ))}
          </select>
          <select
            className="form-input filter-select"
            value={locationTypeFilter}
            onChange={(e) => { setLocationTypeFilter(e.target.value); pagination.reset(); }}
          >
            <option value="">All Location Types</option>
            {locationTypes.map((lt, idx) => (
              <option key={idx} value={lt}>{lt}</option>
            ))}
          </select>
          <select
            className="form-input filter-select"
            value={waterSourceFilter}
            onChange={(e) => { setWaterSourceFilter(e.target.value); pagination.reset(); }}
          >
            <option value="">All Water Sources</option>
            {waterSources.map((ws, idx) => (
              <option key={idx} value={ws}>{ws}</option>
            ))}
          </select>
          <div className="control-group">
            <label>From:</label>
            <input
              type="date"
              className="form-input"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); pagination.reset(); }}
            />
          </div>
          <div className="control-group">
            <label>To:</label>
            <input
              type="date"
              className="form-input"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); pagination.reset(); }}
            />
          </div>
          {(fromDate || toDate || collectorFilter || locationTypeFilter || waterSourceFilter || statusFilter !== 'all') && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setCollectorFilter('');
                setLocationTypeFilter('');
                setWaterSourceFilter('');
                setStatusFilter('all');
                pagination.reset();
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Loading message="Loading samples..." />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>Sample ID</th>
                  <th>Title</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Overall</th>
                  <th>Collected By</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {samples.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center' }}>No samples found</td>
                  </tr>
                ) : (
                  samples.map(sample => (
                    <tr key={sample._id}>
                      <td className="sample-id-cell">{sample.sampleId}</td>
                      <td className="title-cell" title={sample.title}>{sample.title || '-'}</td>
                      <td className="address-cell" title={sample.address}>{sample.address || 'N/A'}</td>
                      <td><StatusBadge status={getDisplayStatus(sample)} /></td>
                      <td>
                        {sample.overallStatus ? (
                          <StatusBadge status={sample.overallStatus} type="condition" />
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td className="collector-cell">{sample.collectedBy?.name || '-'}</td>
                      <td className="date-cell">{new Date(sample.createdAt).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        {(() => {
                          const { middle, danger } = getActionButtons(sample);
                          return (
                            <div className="action-buttons">
                              <span className="action-slot">
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setSelectedSample(sample)}
                                >
                                  View
                                </button>
                              </span>
                              <span className="action-slot">{middle}</span>
                              <span className="action-slot">{danger}</span>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={pagination.goToPage}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
          />
        </>
      )}

      {/* View Sample Modal */}
      <Modal
        isOpen={!!selectedSample}
        onClose={() => setSelectedSample(null)}
        title={`Sample ${selectedSample?.sampleId || ''}`}
        size="large"
      >
        {selectedSample && (
          <div className="sample-modal-content">
            {/* Header with status badges */}
            <div className="sample-modal-header">
              <div className="status-badges">
                <StatusBadge status={getDisplayStatus(selectedSample)} />
                {selectedSample.overallStatus && (
                  <StatusBadge status={selectedSample.overallStatus} type="condition" />
                )}
              </div>
            </div>

            {/* Info Cards */}
            <div className="info-cards">
              <div className="info-card">
                <div className="info-card-icon">📍</div>
                <div className="info-card-content">
                  <span className="info-card-label">Location</span>
                  <span className="info-card-value">{selectedSample.address || 'N/A'}</span>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-icon">👤</div>
                <div className="info-card-content">
                  <span className="info-card-label">Collected By</span>
                  <span className="info-card-value">{selectedSample.collectedBy?.name || 'N/A'}</span>
                </div>
              </div>
              <div className="info-card">
                <div className="info-card-icon">📅</div>
                <div className="info-card-content">
                  <span className="info-card-label">Collection Date</span>
                  <span className="info-card-value">
                    {selectedSample.collectedAt
                      ? new Date(selectedSample.collectedAt).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
              {selectedSample.testInfo?.fieldTested && (
                <div className="info-card">
                  <div className="info-card-icon">🔬</div>
                  <div className="info-card-content">
                    <span className="info-card-label">Field Tested</span>
                    <span className="info-card-value">
                      {selectedSample.testInfo.fieldTestedAt
                        ? new Date(selectedSample.testInfo.fieldTestedAt).toLocaleDateString()
                        : 'Yes'}
                    </span>
                  </div>
                </div>
              )}
              {selectedSample.testInfo?.labTestedBy && (
                <div className="info-card">
                  <div className="info-card-icon">🧪</div>
                  <div className="info-card-content">
                    <span className="info-card-label">Lab Tested By</span>
                    <span className="info-card-value">{selectedSample.testInfo.labTestedBy?.name || 'N/A'}</span>
                  </div>
                </div>
              )}
              {selectedSample.testInfo?.published && (
                <div className="info-card">
                  <div className="info-card-icon">✅</div>
                  <div className="info-card-content">
                    <span className="info-card-label">Published</span>
                    <span className="info-card-value">
                      {selectedSample.testInfo.publishedAt
                        ? new Date(selectedSample.testInfo.publishedAt).toLocaleDateString()
                        : 'Yes'}
                    </span>
                  </div>
                </div>
              )}
              <div className="info-card">
                <div className="info-card-icon">🌐</div>
                <div className="info-card-content">
                  <span className="info-card-label">Coordinates</span>
                  <span className="info-card-value">
                    {getCoordinates(selectedSample)
                      ? `${getCoordinates(selectedSample).latitude.toFixed(4)}, ${getCoordinates(selectedSample).longitude.toFixed(4)}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Map */}
            {getCoordinates(selectedSample) && (
              <div className="map-section">
                <iframe
                  title="Sample Location Map"
                  width="100%"
                  height="180"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: '8px' }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${getCoordinates(selectedSample).longitude - 0.01},${getCoordinates(selectedSample).latitude - 0.01},${getCoordinates(selectedSample).longitude + 0.01},${getCoordinates(selectedSample).latitude + 0.01}&layer=mapnik&marker=${getCoordinates(selectedSample).latitude},${getCoordinates(selectedSample).longitude}`}
                  allowFullScreen
                />
              </div>
            )}

            {/* Images */}
            {(selectedSample.images?.sampleImageUrl || selectedSample.images?.locationImageUrl) && (
              <div className="images-section">
                <h4>Images</h4>
                <div className="images-grid">
                  {selectedSample.images?.sampleImageUrl && (
                    <div className="image-item">
                      <img src={selectedSample.images.sampleImageUrl} alt="Sample" />
                      <span>Sample</span>
                    </div>
                  )}
                  {selectedSample.images?.locationImageUrl && (
                    <div className="image-item">
                      <img src={selectedSample.images.locationImageUrl} alt="Location" />
                      <span>Location</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Parameters - Show FIELD and LAB separately */}
            {selectedSample.parameters?.length > 0 && (
              <div className="parameters-section">
                <h4>Test Results ({selectedSample.parameters.length} parameters)</h4>

                {/* FIELD Parameters - Simple table without Limit/Status */}
                {selectedSample.parameters.filter(p => p.testLocation === 'FIELD').length > 0 && (
                  <>
                    <h5 className="param-section-title">FIELD Test Values</h5>
                    <div className="parameters-table-wrapper">
                      <table className="table parameters-table">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                            <th>Unit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSample.parameters
                            .filter(p => p.testLocation === 'FIELD')
                            .map((param, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="param-name">
                                    {param.code && <code>{param.code}</code>}
                                    <span>{param.name || 'Parameter'}</span>
                                  </div>
                                </td>
                                <td className="param-value">{param.value}</td>
                                <td>{param.unit || '-'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {!selectedSample.testInfo?.labTested && (
                      <p className="field-note">Status will be calculated after LAB test</p>
                    )}
                  </>
                )}

                {/* LAB Parameters - Full table with Limit/Status */}
                {selectedSample.parameters.filter(p => p.testLocation === 'LAB').length > 0 && (
                  <>
                    <h5 className="param-section-title">LAB Test Results</h5>
                    <div className="parameters-table-wrapper">
                      <table className="table parameters-table">
                        <thead>
                          <tr>
                            <th>Parameter</th>
                            <th>Value</th>
                            <th>Unit</th>
                            <th>Limit</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSample.parameters
                            .filter(p => p.testLocation === 'LAB')
                            .map((param, idx) => (
                              <tr key={idx} className={`param-row-${param.status?.toLowerCase()}`}>
                                <td>
                                  <div className="param-name">
                                    <code>{param.code}</code>
                                    <span>{param.name}</span>
                                  </div>
                                </td>
                                <td className="param-value">{param.value}</td>
                                <td>{param.unit}</td>
                                <td className="param-limit">
                                  {param.type === 'RANGE'
                                    ? `${param.acceptableLimit?.min} - ${param.acceptableLimit?.max}`
                                    : param.type === 'MAX'
                                      ? `≤ ${param.acceptableLimit?.max}${param.permissibleLimit?.max ? ` / ≤ ${param.permissibleLimit.max}` : ''}`
                                      : '-'}
                                </td>
                                <td><StatusBadge status={param.status} type="condition" /></td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="modal-action-buttons">
              {getDisplayStatus(selectedSample) === 'FIELD_TESTED' && (
                <button
                  className="btn btn-primary"
                  onClick={() => openLabTestModal(selectedSample)}
                >
                  Submit LAB Test
                </button>
              )}
              {getDisplayStatus(selectedSample) === 'LAB_TESTED' && (
                <button
                  className="btn btn-success"
                  onClick={() => handlePublish(selectedSample)}
                >
                  Publish
                </button>
              )}
              {getDisplayStatus(selectedSample) === 'PUBLISHED' && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleDownloadPDF(selectedSample)}
                    disabled={downloading}
                  >
                    {downloading ? 'Downloading...' : 'Download PDF'}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(selectedSample)}
                  >
                    Delete
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setSelectedSample(null)}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Submit LAB Test Modal */}
      <Modal
        isOpen={showLabModal}
        onClose={() => {
          setShowLabModal(false);
          setLabTargetSample(null);
          setParameterValues({});
        }}
        title={`Submit LAB Test - ${labTargetSample?.sampleId || ''}`}
        size="large"
      >
        {labTargetSample && (
          <div className="submit-form">
            <div className="submit-sample-info">
              <h4>Sample Information</h4>
              <p>
                <strong>Title:</strong> {labTargetSample.title || 'N/A'} |
                <strong> Location:</strong> {labTargetSample.address} |
                <strong> Collected:</strong> {new Date(labTargetSample.collectedAt).toLocaleDateString()}
              </p>
              <div className="testing-location-section" style={{ marginTop: '16px' }}>
                <label className="form-label">Testing Location *</label>
                <select
                  className="form-input"
                  value={testingLocation}
                  onChange={(e) => setTestingLocation(e.target.value)}
                  required
                >
                  <option value="On-Site">On-Site</option>
                  <option value="Laboratory">Laboratory</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
              <div className="remark-section" style={{ marginTop: '16px' }}>
                <label className="form-label">Remark / Observation *</label>
                <textarea
                  className="form-input"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Enter remark for the PDF report (required)"
                  rows="3"
                  required
                  style={{ resize: 'vertical', minHeight: '60px' }}
                />
                <small style={{ color: '#666', fontSize: '11px' }}>
                  This text will appear in the PDF Remark/Observation section. Cannot be empty.
                </small>
              </div>
            </div>

            {labParameters.length === 0 ? (
              <div className="no-parameters-message">
                <p>No active LAB parameters found. Please add LAB parameters first.</p>
              </div>
            ) : (
              <form onSubmit={handleLabTestSubmit}>
                <div className="step-header">
                  <h4>Enter LAB Parameter Values</h4>
                  <p className="step-hint">Fill in the values for parameters you tested. Empty fields will be skipped.</p>
                </div>

                <div className="parameters-input-grid">
                  {labParameters.map(param => (
                    <div
                      key={param._id}
                      className={`parameter-input-card ${parameterValues[param._id] ? 'has-value' : ''}`}
                    >
                      <div className="parameter-header">
                        <span className="parameter-code">{param.code}</span>
                        <span className="parameter-location-badge lab">LAB</span>
                        <div className="parameter-info">
                          <div className="parameter-name">{param.name}</div>
                          <div className="parameter-unit">Unit: {param.unit}</div>
                        </div>
                      </div>

                      {param.type === 'ENUM' ? (
                        <select
                          className="form-select"
                          value={parameterValues[param._id] || ''}
                          onChange={(e) => handleParamValueChange(param._id, e.target.value)}
                        >
                          <option value="">-- Select Value --</option>
                          {param.enumEvaluation && Object.keys(
                            param.enumEvaluation instanceof Map
                              ? Object.fromEntries(param.enumEvaluation)
                              : param.enumEvaluation
                          ).map((val, idx) => (
                            <option key={idx} value={val}>{val}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={param.type === 'TEXT' ? 'text' : 'number'}
                          className="form-input"
                          placeholder={`Enter value (${param.unit})`}
                          value={parameterValues[param._id] || ''}
                          onChange={(e) => handleParamValueChange(param._id, e.target.value)}
                          step="any"
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="submit-form-footer">
                  <div className="submit-stats">
                    <strong>{Object.values(parameterValues).filter(v => v !== '').length}</strong> of {labParameters.length} parameters filled
                  </div>
                  <div className="submit-form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowLabModal(false);
                        setLabTargetSample(null);
                        setParameterValues({});
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : 'Submit LAB Test'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}
      </Modal>

      {deleteTarget && (
        <div
          className="confirm-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                <path d="M10 11v6"></path>
                <path d="M14 11v6"></path>
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
              </svg>
            </div>
            <h3 className="confirm-title">Delete sample?</h3>
            <p className="confirm-message">
              You're about to delete <strong>{deleteTarget.sampleId}</strong>.
              An admin can reverse this action later.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-btn-cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-btn-delete"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Samples;
